import { Component, Vue } from "vue-property-decorator";
import WithRender from "./AllRequestsDetails.html";
import { Inject } from "vue-di-container";
import RequestsOverviewService from "../RequestsOverviewService";
import { Toaster } from "@/Services/toast";
import { OverviewDetailsFilterModel } from "../OverviewDetailsFilterModel";
import {
    assignRowKeys,
    buildOverviewScopePayload,
    downloadExcelFromBase64,
    EMPTY_GUID,
    formatCreatedDate,
    getTodayStr,
    loadOverviewFilterFromStorage,
} from "../overviewDetailsShared";

@WithRender
@Component({
    diProvide: [RequestsOverviewService],
})
export default class AllRequestsDetails extends Vue {
    @Inject(RequestsOverviewService)
    public requestsOverviewService!: RequestsOverviewService;

    public isLoading: boolean = false;
    public rows: any[] = [];
    public totalCount: number = 0;
    public clientItems: string[] = [];
    public senderNameFilter: string = "";
    public clientNameFilter: string = "";
    public requestStatusFilter: number | null = null;

    public pagination: any = {
        page: 1,
        rowsPerPage: 10,
        sortBy: "createdDate",
        descending: true,
    };
    public isSorted: boolean = true;
    public headers: any[] = [];

    private overviewFilter: OverviewDetailsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenantId: EMPTY_GUID,
        providerId: EMPTY_GUID,
        customerType: 0,
        requestType: 0,
    };

    private searchDebounceId: any = null;

    public clientNameMenuProps: Record<string, unknown> = {
        contentClass: "monitor-dashboard-select-menu all-requests-details-client-menu",
        offsetY: true,
        maxHeight: 260,
    };

    public created(): void {
        this.initHeaders();
        loadOverviewFilterFromStorage(this.overviewFilter);
    }

    public async mounted(): Promise<void> {
        if (!this.overviewFilter.dateTo) {
            this.overviewFilter.dateTo = getTodayStr();
        }
        await this.loadClients();
        await this.loadTable();
    }

    public initHeaders(): void {
        this.headers = [
            {
                text: this.$t("monitorDashboard.createdDate"),
                value: "createdDate",
                align: "center",
                sortable: true,
                width: "160",
            },
            {
                text: this.$t("monitorDashboard.requestType"),
                value: "requestType",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.customerType"),
                value: "customerType",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderName"),
                value: "senderName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.clientName"),
                value: "clientName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.requestStatus"),
                value: "requestStatus",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.smsServiceProvider"),
                value: "smsServiceProvider",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.serviceProvider"),
                value: "serviceProvider",
                align: "center",
                sortable: false,
            },
        ];
    }

    public get requestStatusOptions(): Array<{ id: number; name: string }> {
        return [
            { id: 1, name: String(this.$t("monitorDashboard.pendingApproval")) },
            { id: 2, name: String(this.$t("monitorDashboard.approved")) },
            { id: 3, name: String(this.$t("monitorDashboard.rejected")) },
            { id: 4, name: String(this.$t("monitorDashboard.paid")) },
            { id: 5, name: String(this.$t("monitorDashboard.expired")) },
            { id: 6, name: String(this.$t("monitorDashboard.deleted")) },
            { id: 11, name: String(this.$t("monitorDashboard.statusDeactivated")) },
        ];
    }

    private buildDetailsPayload(): any {
        const payload: any = buildOverviewScopePayload(this.overviewFilter);
        payload.senderName = this.senderNameFilter ? this.senderNameFilter.trim() : "";
        payload.clientName = this.clientNameFilter ? this.clientNameFilter.trim() : "";
        payload.pageIndex = (this.pagination.page - 1) >= 0 ? this.pagination.page - 1 : 0;
        payload.pageSize = this.pagination.rowsPerPage || 10;
        payload.sortBy = "CreatedDate";
        payload.sortDirection = this.pagination.descending === true;

        const statusFilter = this.requestStatusFilter != null ? Number(this.requestStatusFilter) : 0;
        if (statusFilter > 0) {
            payload.requestStatusFilter = statusFilter;
        }

        return payload;
    }

    public async loadClients(): Promise<void> {
        const resp = await this.requestsOverviewService.GetAllRequestsDetailsClients(
            buildOverviewScopePayload(this.overviewFilter),
        );
        if (!resp || !resp.isSuccess || !resp.result) {
            this.clientItems = [];
            return;
        }
        this.clientItems = resp.result;
    }

    public async loadTable(): Promise<void> {
        this.isLoading = true;
        const resp = await this.requestsOverviewService.GetAllRequestsDetails(
            this.buildDetailsPayload(),
        );
        this.isLoading = false;

        if (!resp || !resp.isSuccess) {
            if (resp && resp.errorMessage) {
                Toaster.error(resp.errorMessage);
            }
            this.rows = [];
            this.totalCount = 0;
            return;
        }

        this.rows = assignRowKeys(resp.result || []);
        this.totalCount = resp.totalCount != null ? Number(resp.totalCount) : 0;
    }

    public onSenderNameInput(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onClientNameChange(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
            this.searchDebounceId = null;
        }
        this.pagination.page = 1;
        this.loadTable();
    }

    public onClientNameKeyup(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onRequestStatusChange(): void {
        this.pagination.page = 1;
        this.loadTable();
    }

    public async resetLocalFilters(): Promise<void> {
        this.senderNameFilter = "";
        this.clientNameFilter = "";
        this.requestStatusFilter = null;
        this.pagination.page = 1;
        await this.loadTable();
    }

    public async onExport(): Promise<void> {
        const data = await this.requestsOverviewService.ExportAllRequests(
            this.buildDetailsPayload(),
        );
        if (!data || !data.isSuccess || !data.result) {
            if (data && data.errorMessage) {
                Toaster.error(data.errorMessage);
            }
            return;
        }
        downloadExcelFromBase64(data.result, "AllRequests.xlsx");
    }

    public goBack(): void {
        this.$router.push({
            name: "MonitorDashboard",
            query: {
                topTab: "0",
                requestsTab: "0",
            },
        });
    }

    public changeSort(column: string): void {
        if (column !== "createdDate") {
            return;
        }
        if (this.pagination.sortBy === column) {
            this.pagination.descending = !this.pagination.descending;
        } else {
            this.pagination.sortBy = column;
            this.pagination.descending = true;
        }
        this.isSorted = true;
        this.loadTable();
    }

    public async updatePaginate(val: any): Promise<void> {
        this.pagination.page = val.page;
        this.pagination.rowsPerPage = val.rowsPerPage;
        await this.loadTable();
    }

    public formatCreatedDate(value: string): string {
        return formatCreatedDate(value);
    }
}
